/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState, type Dispatch, type SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router';

/* Import API functions */
import {
    EeCheckApiError,
    requestCurrentValueDataset,
    requestExampleDataset,
    requestIntervalValueDataset,
    requestTimeSeriesDataset,
} from '@/api/eeCheckApi';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import { CHECK_API_TOKEN_REQUEST } from '@/config/eeCheckConfig';

/* Import libraries */
import {
    appendDebugEntry,
    createRawResponsePreview,
    createResponsePreview,
    isExpiredSessionError,
} from '@/lib/app/debugRequests';
import { parseIntervalValueCsv } from '@/lib/timeSeries/parseIntervalValueCsv';
import { parseLastValueCsv } from '@/lib/timeSeries/parseLastValueCsv';
import { parseTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import { addSensorTarget, removeSensorTarget } from '@/lib/sensors/demo/sensorTargetList';

/* Import types */
import type { DemoOutputs } from '@/hooks/useDemoOutputs';
import type {
    IntervalValueRequest,
    LastValueRequest,
    StoredPlotlyRequest,
    TimeSeriesRequest,
} from '@/types/eeCheck';
import type { LocalizedMessage } from '@/types/i18n';
import type { DebugRequestEntry } from '@/types/ui';

type Translate = (key: string, values?: Record<string, number | string>) => string;
type ShowStatusModal = (title: LocalizedMessage, message: LocalizedMessage) => void;

interface UseDemoRequestsOptions {
    clearSessionState: (message: LocalizedMessage) => void;
    currentValueRequest: LastValueRequest;
    currentValueTargets: string[];
    intervalValueFrom: string;
    intervalValueRequest: IntervalValueRequest;
    intervalValueTargets: string[];
    intervalValueTo: string;
    navigate: NavigateFunction;
    outputs: DemoOutputs;
    plotlyRequest: StoredPlotlyRequest;
    accessToken: string;
    setCurrentValueTargets: Dispatch<SetStateAction<string[]>>;
    setDebugEntries: Dispatch<SetStateAction<DebugRequestEntry[]>>;
    setIntervalValueTargets: Dispatch<SetStateAction<string[]>>;
    setRequestStatus: Dispatch<SetStateAction<LocalizedMessage>>;
    setSessionStatus: Dispatch<SetStateAction<LocalizedMessage>>;
    onSessionExpired: () => void;
    showStatusModal: ShowStatusModal;
    t: Translate;
    timeSeriesRequest: TimeSeriesRequest;
}

export function useDemoRequests({
    clearSessionState,
    currentValueRequest,
    currentValueTargets,
    intervalValueFrom,
    intervalValueRequest,
    intervalValueTargets,
    intervalValueTo,
    navigate,
    outputs,
    plotlyRequest,
    accessToken,
    setCurrentValueTargets,
    setDebugEntries,
    setIntervalValueTargets,
    setRequestStatus,
    setSessionStatus,
    onSessionExpired,
    showStatusModal,
    t,
    timeSeriesRequest,
}: UseDemoRequestsOptions) {
    const [isCheckingSession, setIsCheckingSession] = useState(false);
    const [isRequestingData, setIsRequestingData] = useState(false);
    const [isRequestingSeries, setIsRequestingSeries] = useState(false);
    const [isRequestingCurrentValue, setIsRequestingCurrentValue] = useState(false);
    const [isRequestingIntervalValues, setIsRequestingIntervalValues] = useState(false);

    function handleAddCurrentValueTarget(target: string) {
        const result = addSensorTarget(currentValueTargets, target);

        if (result.wasDuplicate) {
            showStatusModal({ key: 'Status' }, { key: 'Sensor is already in list.' });
        }

        if (result.wasAdded) {
            setCurrentValueTargets(result.targets);
        }
    }

    function handleRemoveCurrentValueTarget(targetToRemove: string) {
        setCurrentValueTargets((currentTargets) => removeSensorTarget(currentTargets, targetToRemove));
    }

    function handleAddIntervalValueTarget(target: string) {
        const result = addSensorTarget(intervalValueTargets, target);

        if (result.wasDuplicate) {
            showStatusModal({ key: 'Status' }, { key: 'Sensor is already in list.' });
        }

        if (result.wasAdded) {
            setIntervalValueTargets(result.targets);
        }
    }

    function handleRemoveIntervalValueTarget(targetToRemove: string) {
        setIntervalValueTargets((currentTargets) => removeSensorTarget(currentTargets, targetToRemove));
    }

    async function handleCheckSession() {
        if (!accessToken) {
            return;
        }

        setIsCheckingSession(true);
        setRequestStatus({ key: 'Checking API token...' });
        const startedAt = performance.now();

        try {
            const { data, meta } = await requestTimeSeriesDataset(accessToken, CHECK_API_TOKEN_REQUEST);
            const durationMs = Math.round(performance.now() - startedAt);

            appendDebugEntry(
                setDebugEntries,
                meta.url,
                CHECK_API_TOKEN_REQUEST,
                { authorization: 'Bearer [redacted]', method: meta.method },
                true,
                createRawResponsePreview(data, meta.rawResponse),
                durationMs,
            );
            navigate(APP_ROUTES.logindemo);
            outputs.setPlotlyFigure(null);

            if (typeof data === 'string') {
                outputs.setLoginTimeSeriesPreview(parseTimeSeriesCsv(data));
                outputs.setLoginResponseText('');
                outputs.setLoginOutputMode('timeseries');
            } else {
                outputs.setLoginTimeSeriesPreview(null);
                outputs.setLoginResponseText(JSON.stringify(data, null, 2));
                outputs.setLoginOutputMode('raw');
            }

            setSessionStatus({ key: 'API token check succeeded. Session appears valid.' });
            setRequestStatus({
                key: 'API token check completed successfully. ({durationMs} ms)',
                values: { durationMs },
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown request error.');

            appendDebugEntry(
                setDebugEntries,
                error instanceof EeCheckApiError ? error.url : '/internal/api/data-series',
                CHECK_API_TOKEN_REQUEST,
                { authorization: 'Bearer [redacted]', method: 'POST' },
                false,
                createResponsePreview(error instanceof EeCheckApiError ? error.rawResponse : message),
                Math.round(performance.now() - startedAt),
            );

            if (isExpiredSessionError(error)) {
                onSessionExpired();
            }

            navigate(APP_ROUTES.logindemo);
            outputs.setPlotlyFigure(null);
            outputs.setLoginTimeSeriesPreview(null);
            outputs.setLoginOutputMode('raw');
            outputs.setLoginResponseText('');
            clearSessionState({ key: 'API token check failed. Session was cleared.' });
            setRequestStatus({
                key: 'API token check failed: {message}',
                values: { message },
            });
        } finally {
            setIsCheckingSession(false);
        }
    }

    async function handleExampleRequest() {
        if (!accessToken) {
            return;
        }

        setIsRequestingData(true);
        setRequestStatus({ key: 'Requesting example dataset...' });
        const startedAt = performance.now();

        try {
            const { data, meta } = await requestExampleDataset(accessToken, plotlyRequest);
            const durationMs = Math.round(performance.now() - startedAt);

            appendDebugEntry(
                setDebugEntries,
                meta.url,
                plotlyRequest,
                { authorization: 'Bearer [redacted]', method: meta.method },
                true,
                createRawResponsePreview(data, meta.rawResponse),
                durationMs,
            );
            navigate(APP_ROUTES.plotly);
            outputs.setPlotlyFigure(data);
            outputs.setOutputMode('plotly');
            setRequestStatus({
                key: 'Plotly JSON request completed successfully. ({durationMs} ms)',
                values: { durationMs },
            });
            showStatusModal({ key: 'Status' }, { key: 'Plotly JSON was loaded.' });
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown request error.');

            appendDebugEntry(
                setDebugEntries,
                error instanceof EeCheckApiError ? error.url : '/internal/api/stored-plotly-by-tags',
                plotlyRequest,
                { authorization: 'Bearer [redacted]', method: 'POST' },
                false,
                createResponsePreview(error instanceof EeCheckApiError ? error.rawResponse : message),
                Math.round(performance.now() - startedAt),
            );

            if (isExpiredSessionError(error)) {
                onSessionExpired();
            }

            outputs.setPlotlyFigure(null);
            outputs.setOutputMode('empty');
            setRequestStatus({
                key: 'API request failed: {message}',
                values: { message },
            });
        } finally {
            setIsRequestingData(false);
        }
    }

    async function handleTimeSeriesRequest() {
        if (!accessToken) {
            return;
        }

        setIsRequestingSeries(true);
        setRequestStatus({ key: 'Requesting time series dataset...' });
        const startedAt = performance.now();

        try {
            const { data, meta } = await requestTimeSeriesDataset(accessToken, timeSeriesRequest);
            const durationMs = Math.round(performance.now() - startedAt);

            appendDebugEntry(
                setDebugEntries,
                meta.url,
                timeSeriesRequest,
                { authorization: 'Bearer [redacted]', method: meta.method },
                true,
                createRawResponsePreview(data, meta.rawResponse),
                durationMs,
            );
            navigate(APP_ROUTES.timeseries);
            outputs.setPlotlyFigure(null);

            if (typeof data === 'string') {
                outputs.setTimeSeriesPreview(parseTimeSeriesCsv(data));
                outputs.setTimeSeriesResponseText('');
                outputs.setTimeSeriesOutputMode('timeseries');
            } else {
                outputs.setTimeSeriesPreview(null);
                outputs.setTimeSeriesResponseText(JSON.stringify(data, null, 2));
                outputs.setTimeSeriesOutputMode('raw');
            }

            setRequestStatus({
                key: 'Time series API request completed successfully. ({durationMs} ms)',
                values: { durationMs },
            });
            showStatusModal({ key: 'Status' }, { key: 'Time series was loaded.' });
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown request error.');

            appendDebugEntry(
                setDebugEntries,
                error instanceof EeCheckApiError ? error.url : '/internal/api/data-series',
                timeSeriesRequest,
                { authorization: 'Bearer [redacted]', method: 'POST' },
                false,
                createResponsePreview(error instanceof EeCheckApiError ? error.rawResponse : message),
                Math.round(performance.now() - startedAt),
            );

            if (isExpiredSessionError(error)) {
                onSessionExpired();
            }

            outputs.setPlotlyFigure(null);
            outputs.setTimeSeriesPreview(null);
            outputs.setTimeSeriesOutputMode('raw');
            outputs.setTimeSeriesResponseText(error instanceof EeCheckApiError ? error.rawResponse || message : message);
            setRequestStatus({
                key: 'API request failed: {message}',
                values: { message },
            });
        } finally {
            setIsRequestingSeries(false);
        }
    }

    async function handleCurrentValueRequest() {
        if (!accessToken || currentValueTargets.length === 0) {
            return;
        }

        setIsRequestingCurrentValue(true);
        setRequestStatus({ key: 'Requesting current value dataset...' });
        const startedAt = performance.now();

        try {
            const { data, meta } = await requestCurrentValueDataset(accessToken, currentValueRequest);
            const durationMs = Math.round(performance.now() - startedAt);

            appendDebugEntry(
                setDebugEntries,
                meta.url,
                currentValueRequest,
                { authorization: 'Bearer [redacted]', method: meta.method },
                true,
                createRawResponsePreview(data, meta.rawResponse),
                durationMs,
            );
            navigate(APP_ROUTES.currentvalue);
            outputs.setPlotlyFigure(null);

            if (typeof data === 'string') {
                outputs.setCurrentValuePreview(parseLastValueCsv(data));
                outputs.setCurrentValueResponseText('');
                outputs.setCurrentValueOutputMode('currentvalue');
            } else {
                outputs.setCurrentValuePreview(null);
                outputs.setCurrentValueResponseText(JSON.stringify(data, null, 2));
                outputs.setCurrentValueOutputMode('raw');
            }

            setRequestStatus({
                key: 'Current value API request completed successfully. ({durationMs} ms)',
                values: { durationMs },
            });
            showStatusModal(
                { key: 'Status' },
                { key: currentValueTargets.length > 1 ? 'Current values were loaded.' : 'Current value was loaded.' },
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown request error.');

            appendDebugEntry(
                setDebugEntries,
                error instanceof EeCheckApiError ? error.url : '/internal/api/current-values',
                currentValueRequest,
                { authorization: 'Bearer [redacted]', method: 'POST' },
                false,
                createResponsePreview(error instanceof EeCheckApiError ? error.rawResponse : message),
                Math.round(performance.now() - startedAt),
            );

            if (isExpiredSessionError(error)) {
                onSessionExpired();
            }

            outputs.setPlotlyFigure(null);
            outputs.setCurrentValuePreview(null);
            outputs.setCurrentValueOutputMode('raw');
            outputs.setCurrentValueResponseText(error instanceof EeCheckApiError ? error.rawResponse || message : message);
            setRequestStatus({
                key: 'API request failed: {message}',
                values: { message },
            });
        } finally {
            setIsRequestingCurrentValue(false);
        }
    }

    async function handleIntervalValueRequest() {
        if (
            !accessToken ||
            intervalValueTargets.length === 0 ||
            !intervalValueFrom.trim() ||
            !intervalValueTo.trim()
        ) {
            return;
        }

        setIsRequestingIntervalValues(true);
        setRequestStatus({ key: 'Requesting interval value dataset...' });
        const startedAt = performance.now();

        try {
            const { data, meta } = await requestIntervalValueDataset(accessToken, intervalValueRequest);
            const durationMs = Math.round(performance.now() - startedAt);

            appendDebugEntry(
                setDebugEntries,
                meta.url,
                intervalValueRequest,
                { authorization: 'Bearer [redacted]', method: meta.method },
                true,
                createRawResponsePreview(data, meta.rawResponse),
                durationMs,
            );
            navigate(APP_ROUTES.intervalvalues);
            outputs.setPlotlyFigure(null);

            if (typeof data === 'string') {
                outputs.setIntervalValuePreview(parseIntervalValueCsv(data));
                outputs.setIntervalValueResponseText('');
                outputs.setIntervalValueOutputMode('intervalvalues');
            } else {
                outputs.setIntervalValuePreview(null);
                outputs.setIntervalValueResponseText(JSON.stringify(data, null, 2));
                outputs.setIntervalValueOutputMode('raw');
            }

            setRequestStatus({
                key: 'Interval value API request completed successfully. ({durationMs} ms)',
                values: { durationMs },
            });
            showStatusModal({ key: 'Status' }, { key: 'Interval values were loaded.' });
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown request error.');

            appendDebugEntry(
                setDebugEntries,
                error instanceof EeCheckApiError ? error.url : '/internal/api/interval-values',
                intervalValueRequest,
                { authorization: 'Bearer [redacted]', method: 'GET' },
                false,
                createResponsePreview(error instanceof EeCheckApiError ? error.rawResponse : message),
                Math.round(performance.now() - startedAt),
            );

            if (isExpiredSessionError(error)) {
                onSessionExpired();
            }

            outputs.setPlotlyFigure(null);
            outputs.setIntervalValuePreview(null);
            outputs.setIntervalValueOutputMode('raw');
            outputs.setIntervalValueResponseText(error instanceof EeCheckApiError ? error.rawResponse || message : message);
            setRequestStatus({
                key: 'API request failed: {message}',
                values: { message },
            });
        } finally {
            setIsRequestingIntervalValues(false);
        }
    }

    return {
        handleAddCurrentValueTarget,
        handleAddIntervalValueTarget,
        handleCheckSession,
        handleCurrentValueRequest,
        handleExampleRequest,
        handleIntervalValueRequest,
        handleRemoveCurrentValueTarget,
        handleRemoveIntervalValueTarget,
        handleTimeSeriesRequest,
        isCheckingSession,
        isRequestingCurrentValue,
        isRequestingData,
        isRequestingIntervalValues,
        isRequestingSeries,
    };
}
